import React, { useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "redux/store";
import { DashboardLayout } from "component/layout/dashboard";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { LeadTable } from "./lead-table";
import { LoadingIndicator } from "component/shared/loading-indicator";
import {
	getFilteredLeads,
	getLeadsPageFiltersCount,
	getLeadsPageFilters,
	getLeadsPageSort,
	getLeadsPageSearchTerm,
} from "redux/selector";
import {
	updateLeadsPageSort,
	setLeadsPageFilters,
} from "redux/slice/leads-page";
import { setLeadsPageSearchTerm } from "redux/slice/leads-page-search";
import { styles } from "./style";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { FilterDialog } from "./filter-dialog";
import queryString from "query-string";
import { LeadFilterType } from "type/lead-filter";
import { LeadsPageFilters } from "model/leads-page-filters";
import { urls, LeadQuery } from "routes/urls";
import { Sort } from "component/shared/table";
import { debounce } from "debounce";
import { LeadNavigationLocationState } from "shared/lead-navigation";
import { searchLeads } from "shared/lead-list";

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles> {}

const LeadsPageComponent: React.FC<Props> = (props) => {
	const { user, classes = {}, history, location } = props;
	
	const dispatch = useDispatch<any>();

	// Redux state selectors
	const leads = useSelector((state: RootState) => getFilteredLeads(state));
	const filterCount = useSelector((state: RootState) => getLeadsPageFiltersCount(state));
	const filters = useSelector((state: RootState) => getLeadsPageFilters(state));
	const sort = useSelector((state: RootState) => getLeadsPageSort(state));
	const loading = useSelector((state: RootState) => state.leads.loading);
	const term = useSelector((state: RootState) => getLeadsPageSearchTerm(state));

	// set when returning from a lead record opened from this list, which keeps the search
	const [leadsPageReturn] = useState(
		() => (location.state as LeadNavigationLocationState | undefined)?.leadsPageReturn
	);
	const [initialTerm] = useState(leadsPageReturn ? term : "");
	const setTerm = useCallback(
		(newTerm: string) => dispatch(setLeadsPageSearchTerm(newTerm)),
		[dispatch]
	);

	React.useEffect(() => {
		if (!leadsPageReturn) {
			setTerm("");
		}
		// eslint-disable-next-line
	}, []);
	const [leadFilterDialogIsOpen, setLeadFilterDialogIsOpen] = useState(false);
	const [termChanged, setTermChanged] = useState(false);

	const filteredLeads = useMemo(() => searchLeads(leads, term), [leads, term]);

	const filteredLeadCount = filteredLeads.length;

	const debouncedOnTermChange = useMemo(
		() =>
			debounce(
				(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
					const newTerm = event.target.value;
					setTerm(newTerm);
					setTermChanged(true);
					setTimeout(() => setTermChanged(false), 100);
				},
				350
			),
		[]
	);

	const onTermChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			debouncedOnTermChange(event);
		},
		[debouncedOnTermChange]
	);

	const showLeadFilterDialog = useCallback(
		() => setLeadFilterDialogIsOpen(true),
		[]
	);
	const hideLeadFilterDialog = useCallback(
		() => setLeadFilterDialogIsOpen(false),
		[]
	);

	const getFiltersFromParams = useCallback((search: string) => {
				const query = queryString.parse(search);
		const filters: LeadsPageFilters = {};
		Object.keys(query).forEach((name) => {
			if (LeadFilterType.getById(name)) {
				let value: any = query[name];
				if (value === "true") {
					value = true;
				} else if (value === "false") {
					value = false;
				} else if (typeof value === "object") {
					if (name === "pipeline" || name === "agent") {
						value = value.filter((item: any) => !isNaN(item))
							.map((numberString: string) => parseInt(numberString));
					} else if (name === "ranking") {
						value = value.filter((item: any) => !isNaN(item))
							.map((numberString: string) => parseInt(numberString));
					}
				} else if (!isNaN(value)) {
					value = [parseInt(value)];
				} else if (typeof value === "string" ) {
					value = [value];	
				} 
				// @ts-ignore
				filters[name] = value;
			}
		});
		return filters;
	}, []);

	const getSortFromParams = useCallback((search: string): Sort | undefined => {
		const query = queryString.parse(search);
		const value = query["sort"];
		if (typeof value === "string") {
			const id = value.split(":")[0];
			const direction = value.split(":")[1];
			return {
				id,
				desc: direction === "desc",
			};
		}
	}, []);

	const updateUrl = useCallback(() => {
		const query: LeadQuery = {
			leadsPageFilters: filters,
			sort: sort,
		};
		history.push(urls.leads(query));
	}, [filters, sort]);

	React.useEffect(() => {
		const sortParam = getSortFromParams(location.search);
		const filtersParam = getFiltersFromParams(location.search);
		if (Object.keys(filtersParam).length) {
			dispatch(setLeadsPageFilters(filtersParam));
		}
		if (sortParam) {
			dispatch(updateLeadsPageSort(sortParam));
		}
		// eslint-disable-next-line
	}, []);

	React.useEffect(() => {
		updateUrl();
		// eslint-disable-next-line
	}, [filters, sort]);

	const handleLeadCreated = useCallback(
		(leadId: number) => {
			const url = urls.lead(leadId);
			history.push(url);
		},
		[]
	);

	const title = "Leads";

	return (
		<DashboardLayout
			permitted={user && user.permissions.leads}
			title={title}
			header={
				<Mui.Grid container justifyContent="space-between" alignItems="center">
					<Mui.Grid item>
						<Mui.Typography variant="h1">
							{title}
						</Mui.Typography>
					</Mui.Grid>
				</Mui.Grid>
			}
		>
			<FilterDialog
				open={leadFilterDialogIsOpen}
				onClose={hideLeadFilterDialog}
				count={filteredLeadCount}
				buttonText="View"
			/>
			<Mui.Grid container direction="column" spacing={2}>
				<Mui.Grid container direction="column" spacing={2}>
					<Mui.Grid item className={classes.leadsTableContainer}>
						<LeadTable
							termChanged={termChanged}
							user={user}
							leads={filteredLeads}
							onLeadCreated={handleLeadCreated}
							showLeadFilterDialog={showLeadFilterDialog}
							filterCount={filterCount}
							term={term}
							initialTerm={initialTerm}
							onTermChange={onTermChange}
							loading={loading}
							focusLeadId={leadsPageReturn?.leadId}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.Grid>
		</DashboardLayout>
	);
};

export const LeadsPage = withStyles(Router.withRouter(LeadsPageComponent), styles);
